<template>
  <div ref="editorContainer" class="editor-container"></div>
</template>

<script>
import loader from '@monaco-editor/loader'
import { isDarkTheme } from '@/composables/useAppTheme'

export default {
  name: 'DiffCodeViewer',
  props: {
    original: {
      type: String
    },
    modelValue: { // modified
      type: String
    },
    language: {
      type: String,
      default: 'javascript'
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    wordWrap: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      editor: null,
      monaco: null
    }
  },
  computed: {
    monacoTheme () {
      return isDarkTheme() ? 'vs-dark' : 'vs-light'
    }
  },
  mounted () {
    loader.init().then(monaco => {
      this.monaco = monaco

      const originalModel = monaco.editor.createModel(
        this.original,
        this.language
      )

      const modifiedModel = monaco.editor.createModel(
        this.modelValue,
        this.language
      )

      const editor = monaco.editor.createDiffEditor(this.$refs.editorContainer, {
        theme: this.monacoTheme,
        automaticLayout: true,
        readOnly: this.readOnly,
        wordWrap: this.wordWrap ? 'on' : 'off'
      })

      editor.setModel({
        original: originalModel,
        modified: modifiedModel
      })

      this.editor = editor

      this.version = this.editor.getModel().modified.getAlternativeVersionId()

      modifiedModel.onDidChangeContent(() => {
        const value = modifiedModel.getValue()
        this.$emit('update:modelValue', value)

        const dirtyVersion = modifiedModel.getAlternativeVersionId()
        this.$emit('update:isDirty', this.version !== dirtyVersion)
      })
    })
  },
  watch: {
    original (newVal) {
      const model = this.editor?.getModel()?.original
      if (model && newVal !== model.getValue()) {
        model.setValue(newVal)
      }
    },
    modified (newVal) {
      const model = this.editor?.getModel()?.modified
      if (model && newVal !== model.getValue()) {
        model.setValue(newVal)
      }
    },
    language (newLang) {
      const model = this.editor?.getModel()
      if (model) {
        this.monaco.editor.setModelLanguage(model.original, newLang)
        this.monaco.editor.setModelLanguage(model.modified, newLang)
      }
    },
    monacoTheme (newTheme) {
      if (this.monaco) {
        this.monaco.editor.setTheme(newTheme)
      }
    }
  },
  methods: {
    keyDown (event) {
      if (this.$store.state.isWorkflowDisabled) {
        return
      }

      if (this.isDirty && ((event.ctrlKey || event.metaKey) && event.key === 's')) {
        event.preventDefault()
        event.stopPropagation()

        this.version = this.editor.getModel().modified.getAlternativeVersionId()
        this.$emit('update:isDirty', false)
        this.$emit('save')
      }

      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault()
        event.stopPropagation()

        const value = this.editor.getModel().modified.getValue()
        this.$emit('update:original', value)
        this.$emit('update:modelValue', null)
        this.$emit('update:isDirty', false)
        this.$emit('save')
      }
    }
  },
  beforeDestroy () {
    if (this.editor) {
      const model = this.editor.getModel()
      model?.original?.dispose()
      model?.modified?.dispose()
      this.editor.dispose()
    }
  }
}
</script>

<style>
.editor-container {
  width: 100%;
  height: 744px;
}
</style>
